import { useMemo, ViewTransition } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  LawQuestionnaireCategoryRule,
  LawQuestionnaireQuestion,
  LawQuestionnaireQuestionGroup,
  ProductLawApplicabilityStatus,
  ProductLawKey,
} from '@/shared/hooks/api/product'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { V2LawApplicabilityQuestionnaireFieldset } from './V2LawApplicabilityQuestionnaireFieldset'
import {
  getLawQuestionnaireContribution,
  getLawQuestionnaireSectionIntroKey,
  getLawQuestionnaireSectionState,
  getLawQuestionnaireSectionTitleKey,
  getLiveLawQuestionnaireStatus,
  getQuestionDraftAnswer,
  groupLawQuestionnaireQuestions,
  isEng2317QuestionnaireLaw,
  sectionPillBadgeVariant,
  type LawQuestionnaireAnswerDraft,
  type LawQuestionnaireSectionPill,
} from '../utils/lawApplicabilityQuestionnaire'
import {
  localizeLawQuestionnaireQuestions,
  useLawQuestionnaireTranslation,
} from '../utils/localizeLawQuestionnaireQuestion'

function groupExplanationText(
  group: LawQuestionnaireQuestionGroup,
  categoryRule: LawQuestionnaireCategoryRule,
  translate: (key: string) => string
): string {
  if (group === 'categories') {
    if (categoryRule === 'exactlyOne') {
      return translate(
        'v2.applicability.questionnaire.groups.categories.explanationExactlyOne'
      )
    }
    return translate(
      'v2.applicability.questionnaire.groups.categories.explanationAny'
    )
  }
  if (group === 'inScope') {
    return translate(
      'v2.applicability.questionnaire.groups.inScope.explanation'
    )
  }
  return translate(
    'v2.applicability.questionnaire.groups.exclusions.explanation'
  )
}

function verdictBadgeVariant(
  status: ProductLawApplicabilityStatus
): 'success' | 'outline' | 'warning' | 'secondary' {
  if (status === 'APPLICABLE') {
    return 'success'
  }
  if (status === 'NOT_APPLICABLE') {
    return 'outline'
  }
  if (status === 'NEEDS_GRILLING') {
    return 'warning'
  }
  return 'secondary'
}

type GroupNavItemProps = {
  law: ProductLawKey
  group: LawQuestionnaireQuestionGroup
  categoryRule: LawQuestionnaireCategoryRule
  sectionPill: LawQuestionnaireSectionPill
  isSelected: boolean
  onSelect: (group: LawQuestionnaireQuestionGroup) => void
}

function GroupNavItem({
  law,
  group,
  categoryRule,
  sectionPill,
  isSelected,
  onSelect,
}: GroupNavItemProps) {
  const { t } = useTranslation('common')
  let introText = groupExplanationText(group, categoryRule, t)
  if (isEng2317QuestionnaireLaw(law)) {
    introText = t(getLawQuestionnaireSectionIntroKey(law, group))
  }

  return (
    <li>
      <button
        type='button'
        className={cn(
          'w-full rounded border px-4 py-3 text-start transition-colors hover:bg-muted/50',
          isSelected && 'bg-muted'
        )}
        onClick={() => {
          onSelect(group)
        }}
      >
        <div className='flex items-start justify-between gap-2'>
          <span className='text-sm font-medium'>
            {t(getLawQuestionnaireSectionTitleKey(law, group))}
          </span>
          <Badge
            variant={sectionPillBadgeVariant(sectionPill)}
            className='shrink-0'
          >
            {t(`v2.applicability.questionnaire.sectionPills.${sectionPill}`)}
          </Badge>
        </div>
        <p className='mt-1.5 text-xs leading-relaxed text-muted-foreground'>
          {introText}
        </p>
      </button>
    </li>
  )
}

type V2LawApplicabilityQuestionnaireGroupsProps = {
  law: ProductLawKey
  questions: LawQuestionnaireQuestion[]
  draft: LawQuestionnaireAnswerDraft
  categoryRule: LawQuestionnaireCategoryRule
  requiresCompletePackBeforeVerdict: boolean
  selectedGroup: LawQuestionnaireQuestionGroup | null
  remainingCount: number | null
  disabled: boolean
  isPending: boolean
  allowUnset: boolean
  onSelectGroup: (group: LawQuestionnaireQuestionGroup) => void
  onSelectAnswer: (questionKey: string, value: boolean | null) => void
}

export function V2LawApplicabilityQuestionnaireGroups({
  law,
  questions,
  draft,
  categoryRule,
  requiresCompletePackBeforeVerdict,
  selectedGroup,
  remainingCount,
  disabled,
  isPending,
  allowUnset,
  onSelectGroup,
  onSelectAnswer,
}: V2LawApplicabilityQuestionnaireGroupsProps) {
  const { t, i18n } = useLawQuestionnaireTranslation(law)
  const localizedQuestions = useMemo(
    () =>
      localizeLawQuestionnaireQuestions(t, law, questions, i18n.language),
    [t, law, questions, i18n.language]
  )
  const grouped = groupLawQuestionnaireQuestions(localizedQuestions)
  const liveStatus = getLiveLawQuestionnaireStatus(
    localizedQuestions,
    draft,
    categoryRule,
    requiresCompletePackBeforeVerdict
  )
  const selectedEntry = grouped.find((entry) => entry.group === selectedGroup)
  let selectedSectionState = getLawQuestionnaireSectionState(
    law,
    selectedEntry?.group ?? 'inScope',
    localizedQuestions,
    draft,
    categoryRule,
    requiresCompletePackBeforeVerdict
  )
  if (selectedEntry == null) {
    selectedSectionState = getLawQuestionnaireSectionState(
      law,
      'inScope',
      localizedQuestions,
      draft,
      categoryRule,
      requiresCompletePackBeforeVerdict
    )
  }

  return (
    <div className='grid min-h-0 flex-1 overflow-hidden md:grid-cols-[minmax(18rem,26rem)_minmax(0,1fr)]'>
      <aside className='flex min-h-0 flex-col overflow-y-auto border-b p-3 md:border-b-0 md:border-e'>
        <p className='mb-3 px-1 text-sm text-muted-foreground'>
          {t('v2.applicability.questionnaire.argument.appliesIf')}
        </p>
        <ul className='space-y-2'>
          {grouped.map((entry) => {
            const sectionState = getLawQuestionnaireSectionState(
              law,
              entry.group,
              localizedQuestions,
              draft,
              categoryRule,
              requiresCompletePackBeforeVerdict
            )
            return (
              <GroupNavItem
                key={entry.group}
                law={law}
                group={entry.group}
                categoryRule={categoryRule}
                sectionPill={sectionState.pill}
                isSelected={entry.group === selectedGroup}
                onSelect={onSelectGroup}
              />
            )
          })}
        </ul>
        <div className='mt-3 space-y-2 border-t px-1 pt-3'>
          {remainingCount != null && (
            <Badge variant='secondary'>
              {t('v2.applicability.pendingQuestions.remainingCount', {
                count: remainingCount,
              })}
            </Badge>
          )}
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <span className='text-sm font-medium'>
              {t('v2.applicability.questionnaire.argument.verdict')}
            </span>
            <Badge variant={verdictBadgeVariant(liveStatus)}>
              {t(`v2.applicability.status.${liveStatus}`)}
            </Badge>
          </div>
        </div>
      </aside>

      <div className='flex min-h-0 min-w-0 flex-col overflow-y-auto p-4 md:p-6'>
        <ViewTransition key={selectedGroup ?? 'empty'}>
          <div>
            {selectedEntry != null && (
              <section className='space-y-4'>
                <div className='space-y-2'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <h2 className='text-lg font-semibold'>
                      {t(
                        getLawQuestionnaireSectionTitleKey(
                          law,
                          selectedEntry.group
                        )
                      )}
                    </h2>
                    <Badge
                      variant={sectionPillBadgeVariant(selectedSectionState.pill)}
                    >
                      {t(
                        `v2.applicability.questionnaire.sectionPills.${selectedSectionState.pill}`
                      )}
                    </Badge>
                  </div>
                  {isEng2317QuestionnaireLaw(law) ? (
                    <p className='text-sm text-muted-foreground'>
                      {t(
                        getLawQuestionnaireSectionIntroKey(
                          law,
                          selectedEntry.group
                        )
                      )}
                    </p>
                  ) : null}
                  <p className='rounded-md border bg-muted/30 px-3 py-2 text-sm leading-relaxed'>
                    {t(
                      selectedSectionState.copy.key,
                      selectedSectionState.copy.params
                    )}
                  </p>
                </div>
                {selectedEntry.questions.map((question) => (
                  <V2LawApplicabilityQuestionnaireFieldset
                    key={question.questionKey}
                    law={law}
                    question={question}
                    currentAnswer={getQuestionDraftAnswer(question, draft)}
                    contribution={getLawQuestionnaireContribution(
                      question,
                      questions,
                      draft,
                      categoryRule
                    )}
                    disabled={disabled}
                    isPending={isPending}
                    allowUnset={allowUnset}
                    onSelectAnswer={onSelectAnswer}
                  />
                ))}
              </section>
            )}
          </div>
        </ViewTransition>
      </div>
    </div>
  )
}
